import { subDays } from "date-fns";

const Environments = Object.freeze({
  TEST: "test",
  LOCAL: "local",
  ENV_VARS: "env_vars",
});

const FileLimits = {
  uploadSizeLimitBytes: 150 * 1024,
};

const ExternalService = {
  sentryDsn: "https://479d425cc32a4c42a4239580f0143721@sentry.chaserhq.com/13",
};

const PaymentTypes = {
  card: "card",
  bacs_debit: "bacs_debit",
  au_becs_debit: "au_becs_debit",
  sepa_debit: "sepa_debit",
  us_bank_account: "us_bank_account",
  stripe: "STRIPE",
  unipaas: "UNIPAAS",
};

const DebitPaymentTypes = {
  bacs_debit: "bacs_debit",
};

const ddCurrencyMapping = Object.freeze({
  gbp: {
    contactDDMandatePropertyAt: "firstTimeAcceptedBacsAt",
    currencyNameMethod: {
      stripe: "BACS",
    },
    stripeDDMethod: "bacs_debit",
  },
  eur: {
    contactDDMandatePropertyAt: "firstTimeAcceptedSepaAt",
    stripeDDMethod: "sepa_debit",
    currencyNameMethod: {
      stripe: "SEPA",
    },
  },
  aud: {
    contactDDMandatePropertyAt: "firstTimeAcceptedBecsAt",
    stripeDDMethod: "au_becs_debit",
    currencyNameMethod: {
      stripe: "BECS",
    },
  },
  usd: {
    contactDDMandatePropertyAt: "firstTimeAcceptedAchAt",
    stripeDDMethod: "us_bank_account",
    currencyNameMethod: {
      stripe: "ACH",
    },
  },
});

const ddPaymentFailedErrorsMapping = Object.freeze({
  amount_too_large: "Amount too large",
  amount_too_small: "Amount too small",
  balance_insufficient: "Insufficient funds",
  bank_account_declined: "Bank account declined",
  country_unsupported: "Country unsupported",
  debit_not_authorized: "Debit not authorized",
  insufficient_funds: "Insufficient funds",
  payment_method_provider_decline: "Payment method provider declined",
});

// DO NOT CHANGE: Aligned with api
const Integrations = Object.freeze({
  ACCOUNTSIQ: "accountsiq",
  CSV: "csv",
  SAGE: "sage",
  SAGEINTACCT: "sageintacct",
  SAGE200: "sage200",
  SAGE50: "sage50",
  FREEAGENT: "freeagent",
  QUICKBOOKS: "quickbooks",
  DYNAMICS: "dynamics365businesscentral", // Codat Integration with Dynamics 365 Business Central
  D365BC: "d365bc", // Direct Integration with Dynamics 365 Business Central
  SAGECLOUD: "sage200cloud",
  FRESHBOOKS: "freshbooks",
  MYOB: "myobaccountright",
  XERO: "xero",
  ZOHOBOOKS: "zohobooks",
  OPENAPI: "openapi",
  ZAPIER: "zapier",
  NETSUITE: "netsuite",
  ODOO: "odoo",
  STRIPE: "stripe",
});

const DemoIntegrations = Object.freeze({
  OPENAPI: "demo_openapi",
});

const CSVIntegrationTypes = Object.freeze({
  MANUAL: "manual",
  SFTP: "sftp",
});

const OpenAPIIntegrationTypes = {
  [Integrations.OPENAPI]: Integrations.OPENAPI,
  [Integrations.ZAPIER]: Integrations.ZAPIER,
  [Integrations.SAGE50]: Integrations.SAGE50,
};

const BillingProvider = Object.freeze({
  XERO: Integrations.XERO,
  CHARGEBEE: "chargebee",
});

const BillingPeriods = Object.freeze({
  MONTHLY: "month",
  YEARLY: "year",
});

const IntegrationDisplayNames = {
  [Integrations.XERO]: "Xero",
  [Integrations.QUICKBOOKS]: "QuickBooks Online",
  [Integrations.SAGE]: "Sage",
  [Integrations.SAGEINTACCT]: "Sage Intacct",
  [Integrations.SAGE200]: "Sage 200",
  [Integrations.SAGE50]: "Sage 50 Accounts",
  [Integrations.FREEAGENT]: "FreeAgent",
  [Integrations.DYNAMICS]: "MS Dynamics 365 Business Central",
  [Integrations.MYOB]: "MYOB",
  [Integrations.FRESHBOOKS]: "FreshBooks",
  [Integrations.CSV]: "CSV",
  [Integrations.SAGECLOUD]: "Sage 200Cloud",
  [Integrations.ZOHOBOOKS]: "ZohoBooks",
  [Integrations.ACCOUNTSIQ]: "AccountsIQ",
  [Integrations.OPENAPI]: "Chaser API",
  [Integrations.ZAPIER]: "Zapier",
  [Integrations.NETSUITE]: "Oracle NetSuite",
  [Integrations.D365BC]: "Dynamics 365 Business Central",
  [Integrations.ODOO]: "Odoo",
  [Integrations.STRIPE]: "Stripe",
};

const IntegrationCustomerURL = (
  accountingSoftware,
  contactId,
  shortCode,
  accessToken,
) => {
  const URLs = {
    [Integrations.XERO]: `https://go.xero.com/organisationlogin/default.aspx?shortcode=${shortCode}&redirecturl=/Contacts/Edit.aspx?contactID=${contactId}`,
    [Integrations.QUICKBOOKS]: `https://qbo.intuit.com/app/customerdetail?nameId=${contactId.substr(
      2,
    )}`,
    [Integrations.SAGE]: `https://accounts-extra.sageone.com/contacts/contacts/${contactId.substr(
      2,
    )}`,
    [Integrations.FREEAGENT]: `https://${shortCode}.freeagent.com/contacts/${contactId}`,
    [Integrations.DYNAMICS]: "https://businesscentral.dynamics.com/?page=22",
    [Integrations.FRESHBOOKS]: `https://my.freshbooks.com/#/client/${contactId}`,
    [Integrations.MYOB]: undefined,
    [Integrations.CSV]: undefined,
    [Integrations.SAGECLOUD]: undefined,
    [Integrations.ZOHOBOOKS]: undefined,
    [Integrations.ACCOUNTSIQ]: undefined,
    [Integrations.OPENAPI]: undefined,
    [Integrations.NETSUITE]: undefined,
    [Integrations.D365BC]: `https://businesscentral.dynamics.com/?page=21&filter=Customer.%24systemId%20IS%20%27${contactId}%27`,
    [Integrations.ODOO]: `${accessToken.url}/odoo/customers/${contactId}`,
    [Integrations.STRIPE]: `https://dashboard.stripe.com/customers/${contactId}`,
  };

  return URLs[accountingSoftware];
};

// if firebase config doesnt exist, possibly gated by chargebee
const PlanFeaturesByModule = Object.freeze({
  "receivables-module": Object.freeze({
    INVOICES: "invoices",
    SENDERS: "senders",
    TEMPLATES: "templates",
    SCHEDULES: "schedules",
    EMAIL_INTEGRATION_CONNECTIONS: "emailIntegrationConnections",
    ESCALATED_SENDERS: "escalatedSenders",
    ESCALATED_RECIPIENTS: "escalatedRecipients",
    DOMAIN_VERIFICATION: "domainVerification",
    DIRECT_EMAIL: "directEmail",
    CUSTOMER_REPORT: "customerReport",
    STRIPE_DD_PAYMENT: "stripeDdPayment",
    RECOMMENDED_CHASING_TIMES: "recommendedChasingTimes",
    LATE_PAYMENT_INDICATOR: "latePaymentIndicator",
    CREDIT_MONITORING: "creditMonitoring",
    EXPECTED_PAYMENT_DATE: "expectedPaymentDates",
    INVOICE_DISPUTE: "invoiceDispute",
    COLLECTIONS: "collections",
    SMS: "sms",
    CALL_REMINDERS: "callReminders",
    TASKS: "tasks",
    CUSTOMER_TAGS: "customerTags",
    MANUAL_BANK_TRANSFER: "manualBankTransfer",
    CALL_RECORDING: "callRecording",
    TFP_EMAILS: "tfpEmails",
    IN_APP_CALLS: "inAppCalls",
    AUTO_CALL: "autoCall",
    PHONE_CALL: "phoneCall",
    LETTER: "letter",
    IN_PORTAL_ADVERT: "inPortalAdvert",
    REVENUE_FORECASTING: "revenueForecasting",
    PAYMENT_SCORE: "paymentScore",
    PAYMENT_PLANS: "paymentPlans",
    EDIT_SENDERS: "editSenders",
    FEES_AND_DISCOUNTS: "feesAndDiscounts",
    ARTIFICIAL_INTELLIGENCE: "artificialIntelligence",
  }),
  "organisation-module": Object.freeze({
    USERS: "users",
  }),
  "cashflow-module": Object.freeze({}),
});

const PlanFeatures = Object.freeze({
  ...PlanFeaturesByModule["receivables-module"],
  ...PlanFeaturesByModule["organisation-module"],
  ...PlanFeaturesByModule["cashflow-module"],
});

const Features = {
  OUTAGE_BANNER: "outageBanner",
  CUSTOMER_LEVEL_CHASING: "customerLevelChasing",
  ADMIN_ONLY: "adminOnly",
  CREDIT_REPORT: "creditReport",
  DIRECT_DEBIT: "directDebit",
  REIMBURSEMENT_ACCOUNTS: "reimbursementAccounts",
  PORTAL_LOGO_CUSTOMIZATION: "portalLogoCustomization",
  G2_REVIEWS: "g2Reviews",
  STRIPE_BECS_PAYMENT: "stripeBecsPayment",
  RECEIVABLES_FORECAST: "receivablesForecasting",
  AI_ASSISTED_CHAT: "aiAssistedChat",
  CASHFLOW_FORECAST: "cashflowForecasting",
  ...PlanFeatures,
};

const Channels = {
  EMAIL: "email",
  SMS: "sms",
  TASK: "task",
  PHONE_CALL: "phone_call",
  LETTER: "letter",
};

const addonIds = {
  ADDITIONAL_USERS: "additional-users",
  USER: "user",
  TEMPLATE: "template",
  SCHEDULE: "schedule",
  EMAIL_INTEGRATION: "email-integration",
  THANKS_FOR_PAYING: "thanks-for-paying",
  CREDIT_CHECK: "credit-check",
  RECEIVABLES_FORECAST: "receivables-forecasting",
  SMS_CHASING_CREDITS: "sms-chasing-credits",
  PHONE_CALL_CHASING_CREDITS: "phone-call-chasing-credits",
  LETTER_CHASING_CREDITS: "letter-chasing-credits",
};

const ADDON_TYPES = {
  PER_UNIT: "PER_UNIT",
  FLAT_FEE: "FLAT_FEE",
  VOLUME: "VOLUME",
};

const featureToAddonId = {
  [Features.USERS]: addonIds.USER,
  [Features.TEMPLATES]: addonIds.TEMPLATE,
  [Features.SCHEDULES]: addonIds.SCHEDULE,
  [Features.EMAIL_INTEGRATION_CONNECTIONS]: addonIds.EMAIL_INTEGRATION,
  [Features.TFP_EMAILS]: addonIds.THANKS_FOR_PAYING,
  [Features.CREDIT_REPORT]: addonIds.CREDIT_CHECK,
  [Features.SMS]: addonIds.SMS_CHASING_CREDITS,
  [Features.PHONE_CALL]: addonIds.PHONE_CALL_CHASING_CREDITS,
  [Features.LETTER]: addonIds.LETTER_CHASING_CREDITS,
};

const addonIdToFeature = {
  [addonIds.ADDITIONAL_USERS]: PlanFeatures.USERS,
  [addonIds.USER]: PlanFeatures.USERS,
  [addonIds.TEMPLATE]: PlanFeatures.TEMPLATES,
  [addonIds.SCHEDULE]: PlanFeatures.SCHEDULES,
  [addonIds.EMAIL_INTEGRATION]: PlanFeatures.EMAIL_INTEGRATION_CONNECTIONS,
  [addonIds.THANKS_FOR_PAYING]: PlanFeatures.TFP_EMAILS,
  [addonIds.CREDIT_CHECK]: Features.CREDIT_REPORT,
  [addonIds.RECEIVABLES_FORECAST]: Features.RECEIVABLES_FORECAST,
  [addonIds.SMS_CHASING_CREDITS]: Features.SMS,
  [addonIds.PHONE_CALL_CHASING_CREDITS]: Features.PHONE_CALL,
  [addonIds.LETTER_CHASING_CREDITS]: Features.LETTER,
};

const ChannelNames = {
  [Channels.EMAIL]: "Email",
  [Channels.SMS]: "SMS",
  [Channels.PHONE_CALL]: "Auto-call",
  [Channels.LETTER]: "Letter",
  [Channels.TASK]: "Task",
};

const channelToAddonLimitsName = {
  [Channels.EMAIL]: "email",
  [Channels.SMS]: "sms",
  [Channels.PHONE_CALL]: "phoneCall",
  [Channels.LETTER]: "letter",
};

const periodToNoticeTime = {
  [BillingPeriods.MONTHLY]: 30,
  [BillingPeriods.YEARLY]: 90,
};

const PaymentScoreLabelsArray = ["none", "bad", "average", "good"];

const PaymentScoreLabelsTranslate = {
  none: "N/A",
  bad: "Bad",
  good: "Good",
  average: "Average",
};

const EmailProviders = {
  GOOGLE: "google",
  MICROSOFT: "microsoft",
  IMAP: "imap",
};

const TaskTypes = {
  CALL_REMINDER: "call_reminder",
  GET_CREDIT_REPORT: "get_credit_report",
  BOOK_MEETING: "book_meeting",
  VALIDATE_INVOICE_INFORMATION: "validate_invoice_information",
  UPDATE_INVOICE: "update_invoice",
  OFFER_PAYMENT_PLAN: "offer_payment_plan",
  ESCALATE_TO_COLLECTIONS: "escalate_to_collections",
};

const TaskCategories = {
  SETTINGS: "settings",
  SCHEDULE: "schedule",
  CUSTOMER: "customer",
  INVOICE: "invoice",
  CUSTOM: "custom",
};

const TaskStatus = {
  COMPLETED: "COMPLETED",
  IN_PROGRESS: "IN_PROGRESS",
  PENDING: "PENDING",
  CANCELLED: "CANCELLED",
  OPEN: "OPEN",
};

const TaskSource = {
  SCHEDULED: "scheduled",
  MANUAL: "manual",
};

const SupportedReconciliation = [Integrations.XERO, Integrations.QUICKBOOKS];

const SupportedThanksForPayingIntegrations = [
  Integrations.XERO,
  Integrations.QUICKBOOKS,
  Integrations.SAGE,
  Integrations.FREEAGENT,
  Integrations.DYNAMICS,
  Integrations.MYOB,
  Integrations.CSV,
  Integrations.SAGECLOUD,
  Integrations.ACCOUNTSIQ,
  Integrations.NETSUITE,
  Integrations.D365BC,
  Integrations.ODOO,
  Integrations.STRIPE,
];

const AVAILABLE_VIEWS = Object.freeze({
  general: "general",
  detailed: "detailed",
  risk: "risk",
});

const ADDITIONAL_CHARGE_CHANNELS = [
  Channels.SMS,
  Channels.PHONE_CALL,
  Channels.LETTER,
];

const discountTypeOptions = [
  { value: "ONE_TIME_CURRENCY_FEE", label: "Fixed amount (one time)" },
  { value: "ONE_TIME_PERCENTAGE_FEE", label: "Percentage discount (one time)" },
];

const roles = Object.freeze({
  admin: "admin",
  owner: "owner",
  manager: "manager",
  receivablesManager: "receivables_manager",
  receivablesContributor: "receivables_contributor",
  receivablesViewer: "receivables_viewer",
  cashflowManager: "cashflow_manager",
  billing: "billing",
  tester: "tester",
});

const roleNames = {
  [roles.owner]: "Owner",
  [roles.manager]: "Manager",
  [roles.receivablesManager]: "Receivables Manager",
  [roles.receivablesContributor]: "Receivables Contributor",
  [roles.receivablesViewer]: "Receivables Viewer",
  [roles.cashflowManager]: "Cash Flow Manager",
  [roles.billing]: "Billing",
  [roles.admin]: "Admin (Global)",
  [roles.tester]: "Tester",
};

const latePaymentFeesTypeOptions = [
  { value: "BANK_INTEREST_RATE", label: "Bank of England base rate" },
  { value: "ONE_TIME_CURRENCY_FEE", label: "Fixed amount (one time)" },
  { value: "ONE_TIME_PERCENTAGE_FEE", label: "Percentage fee (one time)" },
  { value: "DAILY_PERCENTAGE_FEE", label: "Annual interest % (accrued daily)" },
];

const sendEmailTaxTypeOptions = [
  { label: "No tax", value: "NO_VAT" },
  { label: "Tax inclusive", value: "TAX_INCLUSIVE" },
  { label: "Tax exclusive", value: "TAX_EXCLUSIVE" },
];
const PAYMENT_TIME_ADJUSTMENT_DATE_TO_USE = {
  DueDate: "Due date",
  Date: "Issue date",
};

const AI_SUMMARY_TYPES = {
  dsoOutstanding: "dso-outstanding",
  outstandingAmount: "outstanding-amount",
};

const Modules = Object.freeze({
  RECEIVABLES: "receivables-module",
  CASHFLOW: "cashflow-module",
  ORGANISATION: "organisation-module",
});

const AVAILABLE_ROLES = [
  roles.owner,
  roles.manager,
  roles.receivablesManager,
  roles.receivablesContributor,
  roles.receivablesViewer,
  roles.cashflowManager,
  roles.billing,
];
const ROLE_PRIORITY = {
  [roles.owner]: 1,
  [roles.manager]: 2,
  [roles.receivablesManager]: 3,
  [roles.cashflowManager]: 3,
  [roles.receivablesContributor]: 4,
  [roles.billing]: 4,
  [roles.receivablesViewer]: 5,
};

const GOVERNANCE_ROLES = [roles.owner, roles.manager];

function isGovernanceRole(role) {
  return GOVERNANCE_ROLES.includes(role);
}

function hasGovernanceRole(userRoles) {
  return userRoles.some((r) => isGovernanceRole(r));
}

const roleDescriptions = {
  [roles.owner]:
    "Full platform access. Can transfer ownership, manage billing, and create organisations.",
  [roles.manager]: "Full platform access. Can manage users and roles.",
  [roles.receivablesManager]:
    "Full access to all receivables features including schedules, templates, and settings.",
  [roles.receivablesContributor]:
    "Can manage customers and invoices but cannot change receivables settings.",
  [roles.receivablesViewer]: "Read-only access to receivables data.",
  [roles.cashflowManager]: "Full access to Cash Flow Intelligence features.",
  [roles.billing]: "Can manage subscription and billing settings.",
};

const TASK_SOURCE_DATA_MAP = {
  [TaskSource.SCHEDULED]: {
    tooltip: "Scheduled",
    icon: "calendar",
  },
  [TaskSource.MANUAL]: {
    tooltip: "Manual",
    icon: "user",
  },
};

const ALL_COLLECTIONS_INVOICES = {
  above: 100,
  paid: false,
  overdueBy: subDays(new Date(), 1).toISOString(),
  withoutCollectorStatus: [
    "COLLECTING",
    "COLLECTED",
    "PENDING",
    "WAITING_CONTRACT",
  ],
};

const PHONE_COUNTRY_CODES = [
  { label: "+1 (US/CA)", value: "+1" },
  { label: "+44 (UK)", value: "+44" },
  { label: "+61 (AU)", value: "+61" },
  { label: "+64 (NZ)", value: "+64" },
  { label: "+353 (IE)", value: "+353" },
  { label: "+27 (ZA)", value: "+27" },
  { label: "+33 (FR)", value: "+33" },
  { label: "+49 (DE)", value: "+49" },
  { label: "+31 (NL)", value: "+31" },
  { label: "+46 (SE)", value: "+46" },
  { label: "+47 (NO)", value: "+47" },
  { label: "+45 (DK)", value: "+45" },
  { label: "+358 (FI)", value: "+358" },
];

const ACCOUNTING_SOFTWARE_OPTIONS = [
  { label: "Please select", value: "" },
  { label: "AccountsIQ", value: "AccountsIQ" },
  { label: "Acumatica", value: "Acumatica" },
  { label: "Aptean", value: "Aptean" },
  { label: "Clearbooks", value: "Clearbooks" },
  { label: "Epicor", value: "Epicor" },
  { label: "Exact Online", value: "Exact Online" },
  { label: "FinancialForce", value: "FinancialForce" },
  { label: "FreeAgent", value: "FreeAgent" },
  { label: "FreshBooks", value: "FreshBooks" },
  { label: "iFinance", value: "iFinance" },
  { label: "Infor", value: "Infor" },
  { label: "Iris", value: "Iris" },
  { label: "KashFlow", value: "KashFlow" },
  { label: "Kashoo", value: "Kashoo" },
  { label: "Microsoft Dynamics (Other)", value: "Microsoft Dynamics (Other)" },
  {
    label: "Microsoft Dynamics 365 Business Central",
    value: "Microsoft Dynamics 365 Business Central",
  },
  { label: "MYOB", value: "MYOB" },
  { label: "Odoo", value: "Odoo" },
  { label: "OneAdvance", value: "OneAdvance" },
  { label: "Oracle ERP", value: "Oracle ERP" },
  { label: "Oracle NetSuite", value: "Oracle NetSuite" },
  { label: "Paprika", value: "Paprika" },
  { label: "QuickBooks (Desktop)", value: "QuickBooks (Desktop)" },
  { label: "QuickBooks Online", value: "QuickBooks Online" },
  { label: "Reckon", value: "Reckon" },
  { label: "Sage - Other (Desktop)", value: "Sage - Other (Desktop)" },
  { label: "Sage 100", value: "Sage 100" },
  { label: "Sage 200cloud", value: "Sage 200cloud" },
  { label: "Sage 50 Cloud", value: "Sage 50 Cloud" },
  { label: "Sage 50cloud US", value: "Sage 50cloud US" },
  {
    label: "Sage Business Cloud Accounting (Sage One)",
    value: "Sage Business Cloud Accounting (Sage One)",
  },
  { label: "Sage Intacct", value: "Sage Intacct" },
  { label: "Sage X3 (Live)", value: "Sage X3 (Live)" },
  { label: "SAP Business One", value: "SAP Business One" },
  {
    label: "SAP Business All-in-One (ERP)",
    value: "SAP Business All-in-One (ERP)",
  },
  { label: "Stripe", value: "Stripe" },
  { label: "Wave Accounting", value: "Wave Accounting" },
  { label: "Workday", value: "Workday" },
  { label: "Xero", value: "Xero" },
  { label: "xLedger", value: "xLedger" },
  { label: "Zoho Books", value: "Zoho Books" },
  { label: "Other", value: "Other" },
];

export {
  FileLimits,
  Features,
  PlanFeatures,
  PlanFeaturesByModule,
  Channels,
  ChannelNames,
  Integrations,
  DemoIntegrations,
  CSVIntegrationTypes,
  OpenAPIIntegrationTypes,
  BillingProvider,
  IntegrationDisplayNames,
  PaymentTypes,
  ddCurrencyMapping,
  ddPaymentFailedErrorsMapping,
  DebitPaymentTypes,
  IntegrationCustomerURL,
  ExternalService,
  PaymentScoreLabelsArray,
  PaymentScoreLabelsTranslate,
  EmailProviders,
  Environments,
  SupportedReconciliation,
  TaskTypes,
  TaskStatus,
  TaskCategories,
  TaskSource,
  SupportedThanksForPayingIntegrations,
  BillingPeriods,
  AVAILABLE_VIEWS,
  ADDITIONAL_CHARGE_CHANNELS,
  discountTypeOptions,
  roles,
  latePaymentFeesTypeOptions,
  sendEmailTaxTypeOptions,
  PAYMENT_TIME_ADJUSTMENT_DATE_TO_USE,
  AI_SUMMARY_TYPES,
  addonIds,
  featureToAddonId,
  addonIdToFeature,
  periodToNoticeTime,
  channelToAddonLimitsName,
  ADDON_TYPES,
  Modules,
  AVAILABLE_ROLES,
  ROLE_PRIORITY,
  GOVERNANCE_ROLES,
  isGovernanceRole,
  hasGovernanceRole,
  roleDescriptions,
  TASK_SOURCE_DATA_MAP,
  ALL_COLLECTIONS_INVOICES,
  roleNames,
  PHONE_COUNTRY_CODES,
  ACCOUNTING_SOFTWARE_OPTIONS,
};
